/**
 * 지점 핀 클러스터링 규칙 (설계 §3-3) — **순수 함수**.
 *
 * 상품 지도의 클러스터 반경은 zoom 14/12 두 계단짜리 상수(0.05 / 0.1 / 0.2km)라
 * 나라 단위 줌(5~6)에서는 사실상 묶이지 않는다. 상품 지도는 도시 하나를 보는
 * 화면이라 그래도 됐지만, 지점 허브는 일본 전국 수백 개 지점을 한 화면에 놓고
 * 시작하므로 도쿄·오사카 위에 핀 수백 개가 겹쳐 버린다.
 *
 * 그래서 station variant 는 반경을 줌의 **연속 함수**로 둔다: 줌이 1 줄면 화면이
 * 덮는 실제 거리가 2배가 되므로 반경도 2배가 되어야 "화면에서 본 덩어리 크기"가
 * 일정하게 유지된다.
 */

/**
 * zoom 14에서의 기준 반경(km).
 *
 * 09-16 2차 리뷰에서 0.2 → 0.4 로 넓혔다: zoom 6~7(일본 전국이 한 화면)에서
 * 간사이·간토 핀이 여전히 서로 겹쳐 보였기 때문이다. 0.4면 zoom 7 ≈ 51km 로
 * 광역권 하나가 확실히 한 덩어리가 된다.
 */
const BASE_RADIUS_KM = 0.4;
const BASE_ZOOM = 14;

/** 같은 건물 안 지점까지 갈라지지 않게 하는 하한 */
export const STATION_CLUSTER_MIN_RADIUS_KM = 0.05;
/** 나라 하나가 한 덩어리로 뭉개지지 않게 하는 상한 */
export const STATION_CLUSTER_MAX_RADIUS_KM = 150;

/**
 * station variant 클러스터 반경(km) = `0.4 × 2^(14 − zoom)`, [0.05, 150] 클램프.
 *
 * zoom 6 → 150km(상한), zoom 7 → 51.2km(간토·간사이가 각각 한 덩어리),
 * zoom 12 → 1.6km, zoom 14 → 0.4km, zoom 17 → 0.05km(하한).
 * zoom 이 숫자가 아니면(지도 준비 전) 기준 반경으로 폴백한다.
 */
export function stationClusterRadiusKm(zoom: number): number {
  if (!Number.isFinite(zoom)) return BASE_RADIUS_KM;
  const raw = BASE_RADIUS_KM * Math.pow(2, BASE_ZOOM - zoom);
  return Math.min(
    STATION_CLUSTER_MAX_RADIUS_KM,
    Math.max(STATION_CLUSTER_MIN_RADIUS_KM, raw),
  );
}

/** 클릭 시 "더 당기기" 대신 "낱개로 펼치기"로 넘어가는 경계 */
export const STATION_CLUSTER_EXPAND_MAX_COUNT = 5;
export const STATION_CLUSTER_EXPAND_MIN_ZOOM = 12;

/**
 * 클러스터 클릭이 낱개 핀으로 펼쳐져야 하는가.
 *
 * 충분히 당긴 상태(zoom ≥ 12)에서 지점이 몇 개 안 되는 덩어리는 아무리 더 당겨도
 * 좌표가 거의 같아 갈라지지 않는다 — 그대로 두면 "눌러도 아무 일도 없는데 줌만
 * 계속 올라가는" 무한 확대가 된다. 그 지점을 펼치기로 갈음한다.
 */
export function shouldExpandStationCluster(count: number, zoom: number): boolean {
  return (
    Number.isFinite(count) &&
    Number.isFinite(zoom) &&
    count <= STATION_CLUSTER_EXPAND_MAX_COUNT &&
    zoom >= STATION_CLUSTER_EXPAND_MIN_ZOOM
  );
}
